<template>
  <div class="settings-page">
    <div class="settings-container">
      <div class="settings-sidebar">
        <div class="settings-tabs">
          <button
            :class="['tab', { active: activeTab === 'appearance' }]"
            @click="activeTab = 'appearance'"
          >
            {{ $t('settings.tabs.appearance') }}
          </button>
          <button
            :class="['tab', { active: activeTab === 'behaviour' }]"
            @click="activeTab = 'behaviour'"
          >
            {{ $t('settings.tabs.behaviour') }}
          </button>
          <button
            :class="['tab', { active: activeTab === 'notifications' }]"
            @click="activeTab = 'notifications'"
          >
            {{ $t('settings.tabs.notifications') }}
          </button>
          <button
            :class="['tab', { active: activeTab === 'language' }]"
            @click="activeTab = 'language'"
          >
            {{ $t('settings.tabs.language') }}
          </button>
          <button
            :class="['tab', { active: activeTab === 'parameters' }]"
            @click="activeTab = 'parameters'"
          >
            {{ $t('settings.tabs.parameters') }}
          </button>
          <button
            :class="['tab', { active: activeTab === 'products' }]"
            @click="activeTab = 'products'"
          >
            {{ $t('settings.tabs.products') }}
          </button>
          <button
            :class="['tab', { active: activeTab === 'design' }]"
            @click="activeTab = 'design'"
          >
            {{ $t('settings.tabs.design') }}
          </button>
          <button :class="['tab', { active: activeTab === 'tools' }]" @click="activeTab = 'tools'">
            {{ $t('settings.tabs.tools') }}
          </button>
        </div>
      </div>

      <div class="settings-content">
        <div v-if="activeTab === 'appearance'" class="form-section">
          <h2>{{ $t('settings.tabs.appearance') }}</h2>

          <ToggleInput
            :id="inputs.displayFormTitle.id"
            :label="inputs.displayFormTitle.label"
            v-model="inputs.displayFormTitle.binding"
            :error="inputs.displayFormTitle.error"
            :rules="inputs.displayFormTitle.rules"
            @validate="onValidate('displayFormTitle', $event)"
          />

          <ToggleInput
            :id="inputs.displayPageTitle.id"
            :label="inputs.displayPageTitle.label"
            v-model="inputs.displayPageTitle.binding"
            :error="inputs.displayPageTitle.error"
            :rules="inputs.displayPageTitle.rules"
            @validate="onValidate('displayPageTitle', $event)"
          />

          <ToggleInput
            :id="inputs.displayPageTabs.id"
            :label="inputs.displayPageTabs.label"
            v-model="inputs.displayPageTabs.binding"
            :error="inputs.displayPageTabs.error"
            :rules="inputs.displayPageTabs.rules"
            @validate="onValidate('displayPageTabs', $event)"
          />

          <ToggleInput
            :id="inputs.displayProgress.id"
            :label="inputs.displayProgress.label"
            v-model="inputs.displayProgress.binding"
            :error="inputs.displayProgress.error"
            :rules="inputs.displayProgress.rules"
            @validate="onValidate('displayProgress', $event)"
          />

          <ToggleInput
            :id="inputs.showSaveButton.id"
            :label="inputs.showSaveButton.label"
            v-model="inputs.showSaveButton.binding"
            :error="inputs.showSaveButton.error"
            :rules="inputs.showSaveButton.rules"
            @validate="onValidate('showSaveButton', $event)"
          />

          <div class="form-row">
            <SelectInput
              label="Page progress position"
              v-model="inputs.progressPosition.binding"
              :options="[
                { label: 'Top of form', value: 'top' },
                { label: 'Bottom of form', value: 'bottom' },
              ]"
              @validate="onValidate('progressPosition', $event)"
            />
          </div>

          <!-- Configurator Specific Settings -->
          <div v-if="isConfigurator" class="settings-section-divider"></div>
          <div v-if="isConfigurator">
            <h3>Configurator Specifieke Instellingen</h3>
            <div class="settings-grid">
              <div class="toggle-with-description">
                <ToggleInput
                  id="showPricing"
                  label="Prijzen tonen"
                  v-model="configuratorSettings.showPricing"
                />
                <p class="toggle-description">Toon prijzen tijdens de configuratie</p>
              </div>
              <div class="toggle-with-description">
                <ToggleInput
                  id="showImages"
                  label="Afbeeldingen tonen"
                  v-model="configuratorSettings.showImages"
                />
                <p class="toggle-description">Toon product afbeeldingen</p>
              </div>
              <div class="toggle-with-description">
                <ToggleInput
                  id="enableUpsells"
                  label="Upsells inschakelen"
                  v-model="configuratorSettings.enableUpsells"
                />
                <p class="toggle-description">Toon gerelateerde producten</p>
              </div>
              <div class="toggle-with-description">
                <ToggleInput
                  id="showCategories"
                  label="Productcategorieën tonen"
                  v-model="configuratorSettings.showCategories"
                />
                <p class="toggle-description">
                  Toon categorie tabs om producten te filteren op categorie
                </p>
              </div>
              <div class="toggle-with-description">
                <ToggleInput
                  id="categorizeUpsells"
                  label="Upsells categoriseren"
                  v-model="configuratorSettings.categorizeUpsells"
                />
                <p class="toggle-description">
                  Groepeer upsells op categorie voor betere overzichtelijkheid
                </p>
              </div>
            </div>
          </div>
        </div>

        <div v-if="activeTab === 'behaviour'" class="form-section">
          <h2>{{ $t('settings.tabs.behaviour') }}</h2>
          <div class="form-row">
            <SelectInput
              label="Action on submit"
              v-model="inputs.submitAction.binding"
              :options="[
                { label: 'Redirect to URL', value: 'redirect' },
                { label: 'Display Message', value: 'message' },
              ]"
              @validate="onValidate('progressPosition', $event)"
            />
          </div>

          <div v-if="inputs.submitAction.binding === 'redirect'" class="form-row">
            <TextInput
              :id="inputs.redirectUrl.id"
              :label="inputs.redirectUrl.label"
              v-model="inputs.redirectUrl.binding"
              :error="inputs.redirectUrl.error"
              :rules="inputs.redirectUrl.rules"
              :realTimeValidation="true"
              @validate="onValidate('redirectUrl', $event)"
              placeholder="https://example.com"
            />
          </div>

          <div class="validation-settings">
            <h3>Validation</h3>

            <div class="form-row">
              <ToggleInput
                :id="inputs.validateOnSubmit.id"
                :label="inputs.validateOnSubmit.label"
                v-model="inputs.validateOnSubmit.binding"
                :error="inputs.validateOnSubmit.error"
                :rules="inputs.validateOnSubmit.rules"
                @validate="onValidate('validateOnSubmit', $event)"
              />
              <br />
              <ToggleInput
                :id="inputs.validateWhileTyping.id"
                :label="inputs.validateWhileTyping.label"
                v-model="inputs.validateWhileTyping.binding"
                :error="inputs.validateWhileTyping.error"
                :rules="inputs.validateWhileTyping.rules"
                @validate="onValidate('validateWhileTyping', $event)"
              />
            </div>

            <div class="form-row">
              <ToggleInput
                :id="inputs.autoProgress.id"
                :label="inputs.autoProgress.label"
                v-model="inputs.autoProgress.binding"
                :error="inputs.autoProgress.error"
                :rules="inputs.autoProgress.rules"
                @validate="onValidate('autoProgress', $event)"
              />
            </div>

            <TextInput
              :id="inputs.errorMessage.id"
              :label="inputs.errorMessage.label"
              v-model="inputs.errorMessage.binding"
              :error="inputs.errorMessage.error"
              :rules="inputs.errorMessage.rules"
              :realTimeValidation="true"
              @validate="onValidate('errorMessage', $event)"
              placeholder="Please fix the errors before continuing"
            />
          </div>

          <!-- Configurator Specific Behaviour Settings -->
          <div v-if="isConfigurator" class="settings-section-divider"></div>
          <div v-if="isConfigurator">
            <h3>Deal-instellingen</h3>
            <p class="section-description">
              Configureer wat er gebeurt nadat een klant deze configurator
              afrondt: in welke pipeline een deal terechtkomt en of er
              automatisch een offerte gegenereerd wordt.
            </p>
            <div class="settings-grid">
              <div class="toggle-with-description">
                <ToggleInput
                  id="createDealOnSubmission"
                  label="Automatisch een deal aanmaken"
                  v-model="dealSettings.create_deal"
                />
                <p class="toggle-description">
                  Bij elke nieuwe submission wordt er een deal aangemaakt in
                  de gekozen pipeline. Zet uit om deals handmatig te beheren
                  vanuit de submissie-pagina.
                </p>
              </div>

              <template v-if="dealSettings.create_deal">
                <div class="toggle-with-description">
                  <SelectInput
                    id="dealPipelineId"
                    label="Pipeline"
                    v-model="dealSettings.pipeline_id"
                    :options="pipelineOptions"
                    :placeholder="
                      pipelinesLoading ? 'Pipelines laden...' : 'Kies een pipeline'
                    "
                    :disabled="pipelinesLoading || pipelineOptions.length === 0"
                  />
                  <p
                    v-if="!pipelinesLoading && pipelineOptions.length === 0"
                    class="toggle-description toggle-description--warning"
                  >
                    Er zijn nog geen pipelines aangemaakt. Maak er eerst één
                    aan onder Instellingen → Pipelines.
                  </p>
                </div>

                <div
                  v-if="dealSettings.pipeline_id && stageOptions.length > 0"
                  class="toggle-with-description"
                >
                  <SelectInput
                    id="dealStageId"
                    label="Stage (optioneel)"
                    v-model="dealSettings.stage_id"
                    :options="stageOptions"
                    placeholder="Eerste stage van pipeline"
                  />
                  <p class="toggle-description">
                    Laat leeg om de deal in de eerste stage te plaatsen.
                  </p>
                </div>
              </template>
            </div>

            <!-- Nested: Offerte sub-section. Only relevant when a deal is being
                 auto-created — without a deal there's nothing to attach a
                 proposal to, so we hide (and effectively disable) the whole
                 sub-section. -->
            <div v-if="dealSettings.create_deal" class="nested-section">
              <h4 class="nested-section__title">Offerte</h4>
              <p class="section-description nested-section__description">
                Bepaal of er bij elke submission ook automatisch een offerte
                wordt gegenereerd.
              </p>
              <div class="settings-grid">
                <div class="toggle-with-description">
                  <ToggleInput
                    id="autoCreateProposal"
                    label="Automatisch offerte aanmaken"
                    v-model="configuratorSettings.autoCreateProposal"
                  />
                  <p class="toggle-description">
                    Maak automatisch een offerte aan wanneer een klant de
                    configurator invult. Indien uitgeschakeld, kun je
                    handmatig een offerte aanmaken vanuit de submission.
                  </p>
                </div>
                <div v-if="isMeasurementWorkflow" class="toggle-with-description">
                  <RadioGroup
                    id="proposalType"
                    label="Offertetype bij inzending"
                    v-model="configuratorSettings.proposalType"
                    :options="proposalTypeOptions"
                  />
                  <p class="toggle-description">
                    Voorlopige offertes worden na inmeting definitief gemaakt
                    via de revisie-flow.
                  </p>
                </div>
                <div class="toggle-with-description">
                  <ToggleInput
                    id="showImagesOnProposal"
                    label="Afbeeldingen tonen op offerte"
                    v-model="configuratorSettings.showImagesOnProposal"
                  />
                  <p class="toggle-description">
                    Toon product- en upsell afbeeldingen op de gegenereerde
                    offerte.
                  </p>
                </div>
                <div class="toggle-with-description">
                  <SelectInput
                    id="proposalPriceVisibility"
                    label="Prijsweergave op offerte"
                    v-model="proposalDefaults.price_visibility"
                    :options="priceVisibilityOptions"
                  />
                  <p class="toggle-description">
                    Laat leeg (organisatie-standaard) om de org-instelling te
                    gebruiken, of kies een override voor deze funnel.
                  </p>
                </div>
              </div>
            </div>

            <div class="settings-section-divider"></div>

            <h3>Algemene voorwaarden</h3>
            <p class="section-description">
              Vereis dat klanten akkoord gaan met de algemene voorwaarden voordat ze een offerte
              kunnen aanvragen.
            </p>
            <div class="settings-grid">
              <div class="toggle-with-description">
                <ToggleInput
                  id="requireTermsAcceptance"
                  label="Acceptatie algemene voorwaarden vereisen"
                  v-model="configuratorSettings.requireTermsAcceptance"
                />
                <p class="toggle-description">
                  Klanten moeten akkoord gaan met de algemene voorwaarden voordat ze kunnen
                  verzenden
                </p>
              </div>
            </div>

            <div class="settings-section-divider"></div>

            <h3>Marketing opt-in</h3>
            <p class="section-description">
              Toon een checkbox op de 'Jouw gegevens'-pagina waarmee klanten toestemming geven
              voor e-mails met updates en aanbiedingen.
            </p>
            <div class="settings-grid">
              <div class="toggle-with-description">
                <ToggleInput
                  id="showMarketingOptIn"
                  label="Marketing opt-in tonen"
                  v-model="configuratorSettings.showMarketingOptIn"
                />
                <p class="toggle-description">
                  Klanten geven zelf aan of ze marketing-e-mails willen ontvangen. Alleen wie
                  aanvinkt, ontvangt ze.
                </p>
              </div>
            </div>

            <div class="settings-section-divider"></div>

            <h3>Cart / Multi-Product Offerte</h3>
            <p class="section-description">
              Schakel cart mode in om klanten meerdere producten te laten configureren en toevoegen
              aan een offerte.
            </p>
            <div class="settings-grid">
              <div class="toggle-with-description">
                <ToggleInput
                  id="cartEnabled"
                  label="Cart mode inschakelen"
                  v-model="configuratorSettings.cart.enabled"
                />
                <p class="toggle-description">
                  Klanten kunnen meerdere producten toevoegen aan een offerte
                </p>
              </div>
              <template v-if="configuratorSettings.cart.enabled">
                <div class="toggle-with-description">
                  <ToggleInput
                    id="cartShowPricing"
                    label="Prijzen tonen in cart"
                    v-model="configuratorSettings.cart.showPricing"
                  />
                  <p class="toggle-description">Toon prijzen per product in de cart</p>
                </div>
                <div class="toggle-with-description">
                  <ToggleInput
                    id="cartAllowQuantities"
                    label="Hoeveelheden toestaan"
                    v-model="configuratorSettings.cart.allowQuantities"
                  />
                  <p class="toggle-description">
                    Klanten kunnen hoeveelheden per product aanpassen
                  </p>
                </div>
                <div class="settings-subsection settings-subsection--full">
                  <h4>Aantalregels voor hele offerte</h4>
                  <p class="section-description">
                    Pas korting of toeslag toe op basis van het totaal aantal producten in de cart.
                  </p>
                  <VolumePricingRulesEditor
                    v-model="configuratorSettings.cart.volumePricingRules"
                    :allowed-scopes="['cart_total', 'product_group']"
                    :group-options="volumePricingGroupOptions"
                  />
                </div>
                <div class="toggle-with-description">
                  <SelectInput
                    id="cartShowTotals"
                    label="Totalen weergave"
                    v-model="configuratorSettings.cart.showTotals"
                    :options="[
                      { label: 'Tonen', value: 'show' },
                      { label: 'Blurren', value: 'blur' },
                      { label: 'Verbergen', value: 'hide' },
                      { label: 'Prijs range', value: 'range' },
                    ]"
                  />
                  <p class="toggle-description">Bepaal hoe het totaalbedrag wordt weergegeven</p>
                </div>
                
                <!-- Price Range Settings -->
                <template v-if="configuratorSettings.cart.showTotals === 'range'">
                  <div class="settings-subsection">
                    <div class="toggle-with-description">
                      <ToggleInput
                        id="priceRangeEnabled"
                        label="Prijs range inschakelen"
                        v-model="configuratorSettings.cart.priceRange.enabled"
                      />
                      <p class="toggle-description">Toon een prijs range in plaats van de exacte prijs</p>
                    </div>
                    
                    <template v-if="configuratorSettings.cart.priceRange.enabled">
                      <div class="toggle-with-description">
                        <SelectInput
                          id="priceRangeType"
                          label="Range type"
                          v-model="configuratorSettings.cart.priceRange.type"
                          :options="[
                            { label: 'Percentage (t.o.v. prijs)', value: 'percentage' },
                            { label: 'Vast bedrag', value: 'fixed' },
                            { label: 'Dynamisch (op basis van cart)', value: 'dynamic' },
                          ]"
                        />
                        <p class="toggle-description">Hoe wordt de range berekend?</p>
                      </div>
                      
                      <!-- Percentage type -->
                      <template v-if="configuratorSettings.cart.priceRange.type === 'percentage'">
                        <div class="toggle-with-description">
                          <NumberInput
                            id="priceRangePercentage"
                            label="Percentage"
                            v-model="configuratorSettings.cart.priceRange.percentage"
                            :min="0"
                            :max="100"
                          />
                          <p class="toggle-description">Percentage marge</p>
                        </div>
                        <div class="toggle-with-description">
                          <SelectInput
                            id="priceRangePercentageDirection"
                            label="Marge toepassen"
                            v-model="configuratorSettings.cart.priceRange.percentageDirection"
                            :options="[
                              { label: 'Beide kanten (±X%)', value: 'both' },
                              { label: 'Alleen bovenkant (+X%)', value: 'upper' },
                              { label: 'Alleen onderkant (-X%)', value: 'lower' },
                            ]"
                          />
                          <p class="toggle-description">Waar wordt de marge toegepast?</p>
                        </div>
                      </template>
                      
                      <!-- Fixed type -->
                      <template v-if="configuratorSettings.cart.priceRange.type === 'fixed'">
                        <div class="toggle-with-description">
                          <NumberInput
                            id="priceRangeMinPrice"
                            label="Minimum prijs"
                            v-model="configuratorSettings.cart.priceRange.minPrice"
                            :min="0"
                          />
                          <p class="toggle-description">Vaste minimum prijs</p>
                        </div>
                        <div class="toggle-with-description">
                          <NumberInput
                            id="priceRangeMaxPrice"
                            label="Maximum prijs"
                            v-model="configuratorSettings.cart.priceRange.maxPrice"
                            :min="0"
                          />
                          <p class="toggle-description">Vaste maximum prijs</p>
                        </div>
                      </template>
                      
                      <!-- Dynamic type -->
                      <template v-if="configuratorSettings.cart.priceRange.type === 'dynamic'">
                        <div class="toggle-with-description">
                          <NumberInput
                            id="priceRangeMarginPercentage"
                            label="Marge percentage"
                            v-model="configuratorSettings.cart.priceRange.marginPercentage"
                            :min="0"
                            :max="100"
                          />
                          <p class="toggle-description">Percentage marge toegevoegd aan min/max van cart items</p>
                        </div>
                      </template>
                      
                      <!-- Rounding -->
                      <div class="toggle-with-description">
                        <NumberInput
                          id="priceRangeRoundTo"
                          label="Afronden naar (optioneel)"
                          v-model="configuratorSettings.cart.priceRange.roundTo"
                          :min="0"
                        />
                        <p class="toggle-description">Rond prijzen af naar dichtstbijzijnde bedrag (bijv. 50 voor €50)</p>
                      </div>
                      
                      <!-- Display mode -->
                      <div class="toggle-with-description">
                        <SelectInput
                          id="priceRangeDisplayMode"
                          label="Range weergave"
                          v-model="configuratorSettings.cart.priceRange.displayMode"
                          :options="[
                            { label: 'Volledige range (€800 - €1200)', value: 'full' },
                            { label: 'Alleen minimum (Vanaf €800)', value: 'min-only' },
                            { label: 'Alleen maximum (Tot €1200)', value: 'max-only' },
                            { label: 'Minimum met + (€800+)', value: 'min-plus' },
                            { label: 'Maximum met Max. (Max. €1200)', value: 'max-until' },
                          ]"
                        />
                        <p class="toggle-description">Hoe wordt de range weergegeven?</p>
                      </div>
                      
                      <!-- Custom labels -->
                      <template v-if="configuratorSettings.cart.priceRange.displayMode === 'min-only'">
                        <div class="toggle-with-description">
                          <TextInput
                            id="priceRangeMinLabel"
                            label="Label voor minimum"
                            v-model="configuratorSettings.cart.priceRange.labels.minOnly"
                            placeholder="Vanaf"
                          />
                          <p class="toggle-description">Custom label voor minimum prijs</p>
                        </div>
                      </template>
                      
                      <template v-if="configuratorSettings.cart.priceRange.displayMode === 'max-only'">
                        <div class="toggle-with-description">
                          <TextInput
                            id="priceRangeMaxLabel"
                            label="Label voor maximum"
                            v-model="configuratorSettings.cart.priceRange.labels.maxOnly"
                            placeholder="Tot"
                          />
                          <p class="toggle-description">Custom label voor maximum prijs</p>
                        </div>
                      </template>
                      
                      <template v-if="configuratorSettings.cart.priceRange.displayMode === 'max-until'">
                        <div class="toggle-with-description">
                          <TextInput
                            id="priceRangeMaxUntilLabel"
                            label="Label voor maximum"
                            v-model="configuratorSettings.cart.priceRange.labels.maxUntil"
                            placeholder="Max."
                          />
                          <p class="toggle-description">Custom label voor maximum prijs</p>
                        </div>
                      </template>
                    </template>
                  </div>
                </template>
                <div class="toggle-with-description">
                  <ToggleInput
                    id="cartRevealPricesAfterSubmit"
                    label="Prijzen tonen na aanvraag"
                    v-model="configuratorSettings.cart.revealPricesAfterSubmit"
                  />
                  <p class="toggle-description">
                    Toon de prijzen in de bevestiging nadat de offerte is aangevraagd
                  </p>
                </div>
                
                <!-- Price Range Settings for After Submit -->
                <template v-if="configuratorSettings.cart.revealPricesAfterSubmit === true">
                  <div class="settings-subsection">
                    <div class="toggle-with-description">
                      <ToggleInput
                        id="priceRangeAfterSubmitEnabled"
                        label="Prijs range inschakelen (na aanvraag)"
                        v-model="configuratorSettings.cart.priceRangeAfterSubmit.enabled"
                      />
                      <p class="toggle-description">Toon een prijs range in plaats van de exacte prijs na aanvraag</p>
                    </div>
                    
                    <template v-if="configuratorSettings.cart.priceRangeAfterSubmit.enabled">
                      <div class="toggle-with-description">
                        <SelectInput
                          id="priceRangeAfterSubmitType"
                          label="Range type (na aanvraag)"
                          v-model="configuratorSettings.cart.priceRangeAfterSubmit.type"
                          :options="[
                            { label: 'Percentage (t.o.v. prijs)', value: 'percentage' },
                            { label: 'Vast bedrag', value: 'fixed' },
                            { label: 'Dynamisch (op basis van cart)', value: 'dynamic' },
                          ]"
                        />
                        <p class="toggle-description">Hoe wordt de range berekend?</p>
                      </div>
                      
                      <!-- Percentage type -->
                      <template v-if="configuratorSettings.cart.priceRangeAfterSubmit.type === 'percentage'">
                        <div class="toggle-with-description">
                          <NumberInput
                            id="priceRangeAfterSubmitPercentage"
                            label="Percentage"
                            v-model="configuratorSettings.cart.priceRangeAfterSubmit.percentage"
                            :min="0"
                            :max="100"
                          />
                          <p class="toggle-description">Percentage marge</p>
                        </div>
                        <div class="toggle-with-description">
                          <SelectInput
                            id="priceRangeAfterSubmitPercentageDirection"
                            label="Marge toepassen"
                            v-model="configuratorSettings.cart.priceRangeAfterSubmit.percentageDirection"
                            :options="[
                              { label: 'Beide kanten (±X%)', value: 'both' },
                              { label: 'Alleen bovenkant (+X%)', value: 'upper' },
                              { label: 'Alleen onderkant (-X%)', value: 'lower' },
                            ]"
                          />
                          <p class="toggle-description">Waar wordt de marge toegepast?</p>
                </div>
              </template>
                      
                      <!-- Fixed type -->
                      <template v-if="configuratorSettings.cart.priceRangeAfterSubmit.type === 'fixed'">
                        <div class="toggle-with-description">
                          <NumberInput
                            id="priceRangeAfterSubmitMinPrice"
                            label="Minimum prijs"
                            v-model="configuratorSettings.cart.priceRangeAfterSubmit.minPrice"
                            :min="0"
                          />
                          <p class="toggle-description">Vaste minimum prijs</p>
            </div>
                        <div class="toggle-with-description">
                          <NumberInput
                            id="priceRangeAfterSubmitMaxPrice"
                            label="Maximum prijs"
                            v-model="configuratorSettings.cart.priceRangeAfterSubmit.maxPrice"
                            :min="0"
                          />
                          <p class="toggle-description">Vaste maximum prijs</p>
          </div>
                      </template>
                      
                      <!-- Dynamic type -->
                      <template v-if="configuratorSettings.cart.priceRangeAfterSubmit.type === 'dynamic'">
                        <div class="toggle-with-description">
                          <NumberInput
                            id="priceRangeAfterSubmitMarginPercentage"
                            label="Marge percentage"
                            v-model="configuratorSettings.cart.priceRangeAfterSubmit.marginPercentage"
                            :min="0"
                            :max="100"
                          />
                          <p class="toggle-description">Percentage marge toegevoegd aan min/max van cart items</p>
                        </div>
                      </template>
                      
                      <!-- Rounding -->
                      <div class="toggle-with-description">
                        <NumberInput
                          id="priceRangeAfterSubmitRoundTo"
                          label="Afronden naar (optioneel)"
                          v-model="configuratorSettings.cart.priceRangeAfterSubmit.roundTo"
                          :min="0"
                        />
                        <p class="toggle-description">Rond prijzen af naar dichtstbijzijnde bedrag (bijv. 50 voor €50)</p>
                      </div>
                      
                      <!-- Display mode -->
                      <div class="toggle-with-description">
                        <SelectInput
                          id="priceRangeAfterSubmitDisplayMode"
                          label="Range weergave (na aanvraag)"
                          v-model="configuratorSettings.cart.priceRangeAfterSubmit.displayMode"
                          :options="[
                            { label: 'Volledige range (€800 - €1200)', value: 'full' },
                            { label: 'Alleen minimum (Vanaf €800)', value: 'min-only' },
                            { label: 'Alleen maximum (Tot €1200)', value: 'max-only' },
                            { label: 'Minimum met + (€800+)', value: 'min-plus' },
                            { label: 'Maximum met Max. (Max. €1200)', value: 'max-until' },
                          ]"
                        />
                        <p class="toggle-description">Hoe wordt de range weergegeven?</p>
                      </div>
                      
                      <!-- Custom labels -->
                      <template v-if="configuratorSettings.cart.priceRangeAfterSubmit.displayMode === 'min-only'">
                        <div class="toggle-with-description">
                          <TextInput
                            id="priceRangeAfterSubmitMinLabel"
                            label="Label voor minimum"
                            v-model="configuratorSettings.cart.priceRangeAfterSubmit.labels.minOnly"
                            placeholder="Vanaf"
                          />
                          <p class="toggle-description">Custom label voor minimum prijs</p>
                        </div>
                      </template>
                      
                      <template v-if="configuratorSettings.cart.priceRangeAfterSubmit.displayMode === 'max-only'">
                        <div class="toggle-with-description">
                          <TextInput
                            id="priceRangeAfterSubmitMaxLabel"
                            label="Label voor maximum"
                            v-model="configuratorSettings.cart.priceRangeAfterSubmit.labels.maxOnly"
                            placeholder="Tot"
                          />
                          <p class="toggle-description">Custom label voor maximum prijs</p>
                        </div>
                      </template>
                      
                      <template v-if="configuratorSettings.cart.priceRangeAfterSubmit.displayMode === 'max-until'">
                        <div class="toggle-with-description">
                          <TextInput
                            id="priceRangeAfterSubmitMaxUntilLabel"
                            label="Label voor maximum"
                            v-model="configuratorSettings.cart.priceRangeAfterSubmit.labels.maxUntil"
                            placeholder="Max."
                          />
                          <p class="toggle-description">Custom label voor maximum prijs</p>
                        </div>
                      </template>
                    </template>
                  </div>
                </template>
              </template>
            </div>
          </div>
        </div>

        <div v-if="activeTab === 'notifications'" class="form-section">
          <h2>{{ $t('settings.tabs.notifications') }}</h2>

          <div class="notification-group">
            <div class="notification-group__card">
              <div class="notification-group__header">
                <div class="notification-group__title">
                  <BaseIcon name="envelope" size="20" />
                  <div class="notification-group__title-content">
                    <h3>Self-email notifications</h3>
                    <p class="notification-group__subtitle">
                      Get an email whenever your form is submitted
                    </p>
                  </div>
                </div>
                <ToggleInput
                  :id="inputs.notificationsEnabled.id"
                  v-model="inputs.notificationsEnabled.binding"
                  :error="inputs.notificationsEnabled.error"
                  :rules="inputs.notificationsEnabled.rules"
                  @validate="onValidate('notificationsEnabled', $event)"
                />
              </div>

              <div v-if="inputs.notificationsEnabled.binding" class="notification-group__content">
                <TagInput
                  :id="inputs.notificationRecipients.id"
                  :label="inputs.notificationRecipients.label"
                  v-model="inputs.notificationRecipients.binding"
                  :error="inputs.notificationRecipients.error"
                  :rules="inputs.notificationRecipients.rules"
                  placeholder="you@company.com"
                  @validate="onValidate('notificationRecipients', $event)"
                />
              </div>
            </div>

            <div class="notification-group__card">
              <div class="notification-group__header">
                <div class="notification-group__title">
                  <BaseIcon name="email-bulk" size="20" />
                  <div class="notification-group__title-content">
                    <h3>Recipient email notifications</h3>
                    <p class="notification-group__subtitle">Send an email to the form submitter</p>
                  </div>
                </div>
                <ToggleInput
                  :id="inputs.enableRecipientNotification.id"
                  v-model="inputs.enableRecipientNotification.binding"
                  :error="inputs.enableRecipientNotification.error"
                  :rules="inputs.enableRecipientNotification.rules"
                  @validate="onValidate('enableRecipientNotification', $event)"
                />
              </div>

              <div
                v-if="inputs.enableRecipientNotification.binding"
                class="notification-group__content"
              >
                <SelectInput
                  :id="inputs.recipientEmailFieldId.id"
                  :label="inputs.recipientEmailFieldId.label"
                  v-model="inputs.recipientEmailFieldId.binding"
                  :options="emailFieldOptions"
                  :error="inputs.recipientEmailFieldId.error"
                  :rules="inputs.recipientEmailFieldId.rules"
                  @validate="onValidate('recipientEmailFieldId', $event)"
                />
                <TextAreaInput
                  :id="inputs.recipientEmailMessage.id"
                  :label="inputs.recipientEmailMessage.label"
                  v-model="inputs.recipientEmailMessage.binding"
                  :error="inputs.recipientEmailMessage.error"
                  :rules="inputs.recipientEmailMessage.rules"
                  placeholder="Bedankt voor uw aanvraag voor een [product]. Binnen 1 werkdag ontvangt u de offerte in uw mailbox."
                  @validate="onValidate('recipientEmailMessage', $event)"
                />
                <p class="notification-group__hint">
                  Gebruik [product] voor de gekozen productnaam. Laat leeg voor de standaardtekst.
                </p>
                <ToggleInput
                  :id="inputs.recipientHidePrices.id"
                  :label="inputs.recipientHidePrices.label"
                  v-model="inputs.recipientHidePrices.binding"
                  :error="inputs.recipientHidePrices.error"
                  :rules="inputs.recipientHidePrices.rules"
                  @validate="onValidate('recipientHidePrices', $event)"
                />
                <p class="notification-group__hint">
                  Zet dit aan als de prijs pas in de offerte mag staan, niet in de bevestiging.
                </p>
              </div>
            </div>
          </div>
        </div>
        <div v-if="activeTab === 'language'" class="form-section">
          <h2>{{ $t('settings.tabs.language') }}</h2>

          <div class="form-row">
            <TextInput
              :id="inputs.nextButtonText.id"
              :label="inputs.nextButtonText.label"
              v-model="inputs.nextButtonText.binding"
              :error="inputs.nextButtonText.error"
              :rules="inputs.nextButtonText.rules"
              @validate="onValidate('nextButtonText', $event)"
            />
            <TextInput
              :id="inputs.prevButtonText.id"
              :label="inputs.prevButtonText.label"
              v-model="inputs.prevButtonText.binding"
              :error="inputs.prevButtonText.error"
              :rules="inputs.prevButtonText.rules"
              @validate="onValidate('prevButtonText', $event)"
            />
            <TextInput
              :id="inputs.submitButtonText.id"
              :label="inputs.submitButtonText.label"
              v-model="inputs.submitButtonText.binding"
              :error="inputs.submitButtonText.error"
              :rules="inputs.submitButtonText.rules"
              @validate="onValidate('submitButtonText', $event)"
            />
          </div>
        </div>
        <div v-if="activeTab === 'parameters'" class="form-section">
          <h2>{{ $t('settings.tabs.parameters') }}</h2>
          <p class="section-description">
            Define URL parameters that can be used in your form. These parameters can be accessed
            via the URL like: ?paramName=value
          </p>

          <div class="parameters">
            <div
              v-for="(param, index) in inputs.parameters.binding"
              :key="index"
              class="parameters__item"
            >
              <div class="parameters__fields">
                <TextInput
                  :id="'param-name-' + index"
                  label="Parameter Name"
                  v-model="param.name"
                  :error="param.nameError"
                  :rules="['required']"
                  placeholder="e.g. product"
                  @validate="validateParameterName(index, $event)"
                />
                <TextInput
                  :id="'param-description-' + index"
                  label="Description"
                  v-model="param.description"
                  placeholder="e.g. Product name to pre-fill in the form"
                />
              </div>
              <button class="parameters__remove" @click="removeParameter(index)">
                <BaseIcon name="xmark" size="16" />
              </button>
            </div>
          </div>

          <div class="parameters__add">
            <Btn text="Add parameter" @click="addParameter" variant="secondary" icon="plus" />
          </div>

          <div class="parameters__help" v-if="inputs.parameters.binding.length > 0">
            <h3 class="parameters__help-title">How to use parameters</h3>
            <p class="parameters__help-text">
              You can use these parameters in your form in several ways:
            </p>
            <ul class="parameters__help-list">
              <li>As default values for fields</li>
              <li>In text elements using {parameter_name}</li>
              <li>In formulas and conditions</li>
            </ul>
            <p class="parameters__help-text">Example URL with parameters:</p>
            <code class="parameters__example">
              {{ getExampleUrl() }}
            </code>
          </div>
        </div>
        <div v-if="activeTab === 'tools'" class="form-section">
          <h2>{{ $t('settings.tabs.tools') }}</h2>

          <div class="tools-section">
            <h3>Export/Import Form</h3>

            <div class="form-row">
              <Btn text="Export form data" @click="exportFormData" :loading="exporting" />
            </div>

            <div class="form-row">
              <label class="file-input-label">Import Form Data</label>
              <input
                type="file"
                accept=".csv,.xlsx"
                @change="handleFileImport"
                class="file-input"
              />
            </div>
          </div>
        </div>

        <!-- Products Tab (Configurator only) -->
        <div v-if="isConfigurator && activeTab === 'products'" class="form-section">
          <h2>Producten</h2>
          <p class="section-description">
            Kies de producten die beschikbaar moeten zijn in deze configurator
          </p>

          <div class="product-selection">
            <div v-if="productsLoading" class="loading-state">
              <LoadingSpinner />
              <span>Producten laden...</span>
            </div>

            <EmptyState
              v-else-if="!availableProductsForSelection.length"
              title="Geen producten beschikbaar"
              description="Maak eerst producten aan voordat je ze kunt selecteren."
              icon="box"
            >
              <Btn
                text="Naar Producten"
                variant="secondary"
                @click="handleGoToProducts"
                type="button"
              />
            </EmptyState>

            <div v-else class="product-selection__main">
              <!-- Geselecteerde producten: sleepbare kaarten (volgorde = volgorde in configurator) -->
              <div v-if="selectedProductsData.length" class="selected-products-draggable">
                <h4 class="selected-products-draggable__title">
                  Geselecteerde producten (sleep om volgorde te wijzigen)
                </h4>
                <draggable
                  v-model="selectedProductsData"
                  item-key="productId"
                  handle=".product-card__handle"
                  class="selected-products-draggable__list"
                  ghost-class="product-card--ghost"
                  drag-class="product-card--dragging"
                  @end="hasChanges = true"
                >
                  <template #item="{ element }">
                    <div
                      v-if="getProduct(element.productId)"
                      class="product-card product-card--selected product-card--in-order"
                    >
                      <span
                        class="product-card__handle"
                        aria-label="Sleep om te rangschikken"
                        @click.stop
                      >
                        <BaseIcon name="grip-dots" size="18" />
                      </span>
                      <div class="product-card__image">
                        <img
                          v-if="getProduct(element.productId).imageUrl"
                          :src="getProduct(element.productId).imageUrl"
                          :alt="getProductName(element.productId)"
                          @error="handleProductImageError"
                        />
                        <div v-else class="product-card__placeholder">
                          <BaseIcon name="image" size="24" />
                        </div>
                      </div>
                      <div class="product-card__content">
                        <div class="product-card__header">
                          <h4>{{ getProductName(element.productId) }}</h4>
                          <div class="most-chosen-control" @click.stop>
                            <ToggleInput
                              :id="`most-chosen_${element.productId}`"
                              :model-value="element.isMostChosen"
                              @update:model-value="(value) => toggleMostChosen(element.productId, value)"
                              @click.stop
                              label="Meest gekozen"
                            />
                          </div>
                        </div>
                        <p v-if="getProduct(element.productId).description" class="product-description">
                          {{ getProduct(element.productId).description }}
                        </p>
                        <div class="product-meta">
                          <span class="product-options">
                            {{ getProduct(element.productId).options?.length || 0 }} opties
                          </span>
                          <span
                            v-if="getProduct(element.productId).price"
                            class="product-price"
                          >
                            €{{ formatProductPrice(getProduct(element.productId).price) }}
                          </span>
                        </div>
                      </div>
                      <Btn
                        variant="plain"
                        icon="xmark"
                        size="sm"
                        class="product-card__remove"
                        :aria-label="'Verwijderen'"
                        @click="toggleProduct(element.productId)"
                      />
                    </div>
                  </template>
                </draggable>
              </div>

              <!-- Overige producten: klik om toe te voegen -->
              <div v-if="unselectedProducts.length" class="product-grid-wrapper">
                <h4 v-if="selectedProductsData.length" class="product-grid-wrapper__title">
                  Meer producten toevoegen
                </h4>
                <div class="product-grid">
                  <div
                    v-for="product in unselectedProducts"
                    :key="product.id"
                    class="product-card"
                    @click="toggleProduct(product.id)"
                  >
                    <div class="product-card__image">
                      <img
                        v-if="product.imageUrl"
                        :src="product.imageUrl"
                        :alt="product.title || product.name"
                        @error="handleProductImageError"
                      />
                      <div v-else class="product-card__placeholder">
                        <BaseIcon name="image" size="24" />
                      </div>
                    </div>
                    <div class="product-card__content">
                      <div class="product-card__header">
                        <h4>{{ product.title || product.name }}</h4>
                      </div>
                      <p v-if="product.description" class="product-description">
                        {{ product.description }}
                      </p>
                      <div class="product-meta">
                        <span class="product-options">{{ product.options?.length || 0 }} opties</span>
                        <span v-if="product.price" class="product-price">
                          €{{ formatProductPrice(product.price) }}
                        </span>
                      </div>
                    </div>
                    <div class="product-card__checkbox">
                      <CheckboxInput
                        :id="`product_${product.id}`"
                        :model-value="false"
                        @click.stop
                        @update:model-value="toggleProduct(product.id)"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div v-if="selectedProductsData.length" class="selected-summary">
              <strong>{{ selectedProductsData.length }} product(en) geselecteerd</strong>
            </div>
          </div>
        </div>

        <!-- Design Tab (Configurator only) -->
        <div v-if="isConfigurator && activeTab === 'design'" class="form-section">
          <h2>Design</h2>
          <p class="section-description">
            Kies het design dat gebruikt wordt voor deze configurator
          </p>

          <div class="design-selection">
            <div class="current-design" v-if="currentDesign">
              <div class="design-preview">
                <div
                  class="design-color-preview"
                  :style="{ backgroundColor: currentDesign.colors?.brand?.primary || '#3B82F6' }"
                ></div>
                <div class="design-info">
                  <span class="design-name">{{ currentDesign.name }}</span>
                  <span class="design-category">{{ currentDesign.category || 'Custom' }}</span>
                </div>
              </div>
            </div>

            <div class="design-options">
              <div class="designs-grid">
                <div
                  v-for="design in availableDesigns"
                  :key="design.id"
                  class="design-option"
                  :class="{ 'design-option--active': selectedDesignId === design.id }"
                  @click="selectDesign(design.id)"
                >
                  <div
                    class="design-color"
                    :style="{ backgroundColor: design.colors?.brand?.primary || '#3B82F6' }"
                  ></div>
                  <div class="design-details">
                    <span class="design-name">{{ design.name }}</span>
                    <span class="design-category">{{ design.category || 'Custom' }}</span>
                  </div>
                  <div class="design-option__actions">
                    <Btn
                      variant="plain"
                      size="sm"
                      icon="edit"
                      class="design-edit-btn"
                      @click.stop="handleEditDesign(design)"
                    />
                    <div v-if="selectedDesignId === design.id" class="design-selected">
                      <BaseIcon name="check" />
                    </div>
                  </div>
                </div>
              </div>

              <div class="design-actions">
                <Btn
                  variant="outlined"
                  @click="selectDesign(null)"
                  :class="{ 'btn--active': selectedDesignId === null }"
                >
                  Standaard Design
                </Btn>
                <Btn variant="secondary" @click="handleCreateDesign">
                  <BaseIcon name="plus" />
                  Nieuw Design
                </Btn>
              </div>
            </div>
          </div>
        </div>

        <div class="settings-footer">
          <Btn
            :text="saving ? 'Saving...' : 'Save Changes'"
            @click="saveSettings"
            :disabled="!hasChanges"
            :loading="saving"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import { ref, onMounted, computed, watch } from 'vue';
  import { useRoute, useRouter } from 'vue-router';
  import { useFormStore } from '@/store/formStore';
  import { useUserStore } from '@/store/userStore';
  import { useValidation } from '@/composables/useValidation';
  import { useI18n } from '@/composables/useI18n';
  import { debounce } from 'lodash';
  import * as XLSX from 'xlsx';
  import Papa from 'papaparse';

  import TextInput from '@/components/inputs/TextInput.vue';
  import TextAreaInput from '@/components/inputs/TextAreaInput.vue';
  import ToggleInput from '@/components/inputs/ToggleInput.vue';
  import SelectInput from '@/components/inputs/SelectInput.vue';
  import RadioGroup from '@/components/inputs/RadioGroup.vue';
  import NumberInput from '@/components/inputs/NumberInput.vue';
  import Btn from '@/components/partials/Btn.vue';
  import TagInput from '@/components/inputs/TagInput.vue';
  import CheckboxInput from '@/components/inputs/CheckboxInput.vue';
  import BaseIcon from '@shared/components/BaseIcon.vue';
  import { useDesignStore } from '@/store/designStore';
  import { useUIStore } from '@/store/uiStore';
  import { useToastStore } from '@/store/toastStore';
  import { useDealStore } from '@/store/dealStore';
  import { PROPOSAL_WORKFLOW } from '@/constants/proposalTypes';
  import SidebarDesign from '@/components/editor/sidebar/general/SidebarDesign.vue';
  import LoadingSpinner from '@shared/components/LoadingSpinner.vue';
  import EmptyState from '@/components/partials/EmptyState.vue';
  import VolumePricingRulesEditor from '@/components/products/VolumePricingRulesEditor.vue';
  import draggable from 'vuedraggable';

  export default {
    components: {
      TextInput,
      TextAreaInput,
      ToggleInput,
      Btn,
      SelectInput,
      RadioGroup,
      NumberInput,
      TagInput,
      BaseIcon,
      CheckboxInput,
      LoadingSpinner,
      EmptyState,
      VolumePricingRulesEditor,
      draggable,
    },
    setup() {
      const route = useRoute();
      const router = useRouter();
      const formStore = useFormStore();
      const designStore = useDesignStore();
      const uiStore = useUIStore();
      const toastStore = useToastStore();
      const userStore = useUserStore();
      const dealStore = useDealStore();
      const { t } = useI18n();
      const formId = route.params.id;
      const currentForm = computed(() => formStore.currentForm);
      const programmaticUpdate = ref(false);
      const exporting = ref(false);

      const isConfigurator = computed(() => {
        return currentForm.value?.type === 'configurator';
      });

      const isMeasurementWorkflow = computed(() => {
        return userStore.currentOrganization?.settings?.proposal_workflow === PROPOSAL_WORKFLOW.MEASUREMENT;
      });

      const proposalTypeOptions = [
        { value: 'definitive', label: t('proposals.types.definitive') || 'Definitieve offerte' },
        { value: 'proforma', label: t('proposals.types.proforma') || 'Voorlopige offerte' },
      ];

      // '' = inherit org default (stored as null in proposal_defaults)
      const priceVisibilityOptions = [
        { value: '', label: 'Organisatie-standaard' },
        { value: 'line_items', label: 'Alle regelprijzen' },
        { value: 'main_products', label: 'Alleen totaal per hoofdproduct' },
        {
          value: 'configuration_subtotals',
          label: 'Subtotaal per configuratie',
        },
        { value: 'grand_total', label: 'Alleen eindtotaal' },
      ];

      const proposalDefaults = ref({
        price_visibility: currentForm.value?.settings?.proposal_defaults?.price_visibility || '',
      });

      const activeTab = ref(route.params.tab || 'appearance');

      watch(activeTab, (newTab) => {
        if (newTab !== route.params.tab) {
          router.replace(`/flow/${formId}/settings/${newTab}`);
        }
      });

      watch(
        () => route.params.tab,
        (newTab) => {
          const validTabs = [
            'appearance',
            'behaviour',
            'notifications',
            'language',
            'parameters',
            'tools',
          ];
          if (isConfigurator.value) {
            validTabs.push('products', 'design');
          }
          if (newTab && validTabs.includes(newTab)) {
            activeTab.value = newTab;
          } else {
            router.replace(`/flow/${formId}/settings/appearance`);
          }
        }
      );

      const { inputs, validateForm, onValidate, markTouched } = useValidation({
        displayFormTitle: {
          id: 'displayFormTitle',
          label: 'Display form title',
          binding: false,
          error: null,
          rules: [],
          touched: false,
        },
        displayPageTitle: {
          id: 'displayPageTitle',
          label: 'Display page title',
          binding: false,
          error: null,
          rules: [],
          touched: false,
        },
        displayPageTabs: {
          id: 'displayPageTabs',
          label: 'Display page tabs',
          binding: true,
          error: null,
          rules: [],
          touched: false,
        },
        displayProgress: {
          id: 'displayProgress',
          label: 'Display progress',
          binding: true,
          error: null,
          rules: [],
          touched: false,
        },
        progressPosition: {
          id: 'progressPosition',
          label: 'Progress position',
          binding: 'top',
          error: null,
          rules: ['required'],
          touched: false,
        },
        showSaveButton: {
          id: 'showSaveButton',
          label: 'Show save button',
          binding: false,
          error: null,
          rules: [],
          touched: false,
        },
        autoProgress: {
          id: 'autoProgress',
          label: 'Auto-progress when all fields are pre-filled',
          binding: false,
          error: null,
          rules: [],
          touched: false,
        },
        submitAction: {
          id: 'submitAction',
          label: 'Submit action',
          binding: 'message',
          error: null,
          rules: ['required'],
          touched: false,
        },
        redirectUrl: {
          id: 'redirectUrl',
          label: 'Redirect URL',
          binding: '',
          error: null,
          rules: ['url'],
          touched: false,
        },
        validateOnSubmit: {
          id: 'validateOnSubmit',
          label: 'Validate on submit',
          binding: true,
          error: null,
          rules: [],
          touched: false,
        },
        validateWhileTyping: {
          id: 'validateWhileTyping',
          label: 'Validate while typing',
          binding: false,
          error: null,
          rules: [],
          touched: false,
        },
        errorMessage: {
          id: 'errorMessage',
          label: 'Error message',
          binding: 'Please fix the errors before continuing',
          error: null,
          rules: ['required'],
          touched: false,
        },
        notificationsEnabled: {
          id: 'notificationsEnabled',
          label: 'Enable self-notifications',
          binding: true,
          error: null,
          rules: [],
          touched: false,
        },
        notificationRecipients: {
          id: 'notificationRecipients',
          label: 'Notification recipients (press enter to separate)',
          binding: '',
          error: null,
          rules: [],
          touched: false,
        },
        nextButtonText: {
          id: 'nextButtonText',
          label: 'Next button text',
          binding: 'Next',
          error: null,
          rules: [],
          touched: false,
        },
        prevButtonText: {
          id: 'prevButtonText',
          label: 'Previous button text',
          binding: 'Previous',
          error: null,
          rules: [],
          touched: false,
        },
        submitButtonText: {
          id: 'submitButtonText',
          label: 'Submit button text',
          binding: 'Submit',
          error: null,
          rules: [],
          touched: false,
        },
        enableRecipientNotification: {
          id: 'enableRecipientNotification',
          label: 'Enable recipient notification',
          binding: false,
          error: null,
          rules: [],
          touched: false,
        },
        recipientEmailFieldId: {
          id: 'recipientEmailFieldId',
          label: 'Recipient email field',
          binding: null,
          error: null,
          rules: ['required'],
          touched: false,
        },
        recipientEmailMessage: {
          id: 'recipientEmailMessage',
          label: 'Recipient email message',
          binding: '',
          error: null,
          rules: [],
          touched: false,
        },
        recipientHidePrices: {
          id: 'recipientHidePrices',
          label: 'Prijs weglaten uit bevestigingsmail',
          binding: false,
          error: null,
          rules: [],
          touched: false,
        },
        recipientButtonEnabled: {
          id: 'recipientButtonEnabled',
          label: 'Show button in recipient email',
          binding: false,
          error: null,
          rules: [],
          touched: false,
        },
        recipientButtonText: {
          id: 'recipientButtonText',
          label: 'Button text',
          binding: '',
          error: null,
          rules: [],
          touched: false,
        },
        recipientButtonLink: {
          id: 'recipientButtonLink',
          label: 'Button link',
          binding: '',
          error: null,
          rules: ['url'],
          touched: false,
        },
        parameters: {
          id: 'parameters',
          label: 'URL Parameters',
          binding: [],
          error: null,
          rules: [],
          touched: false,
        },
      });

      const saving = ref(false);
      const hasChanges = ref(false);
      const originalSettings = ref({});
      const originalConfiguratorSettings = ref({});
      const originalDealSettings = ref({});
      const originalProposalDefaults = ref({});
      const originalDesignId = ref(null);

      // Deal-creation settings (lives at settings.deal_settings, NOT inside cpq).
      // Default `create_deal=true` for new configurators — most users will want
      // every submission to spawn a deal in their default pipeline.
      const dealSettings = ref({
        create_deal: currentForm.value?.settings?.deal_settings?.create_deal ?? true,
        pipeline_id: currentForm.value?.settings?.deal_settings?.pipeline_id ?? null,
        stage_id: currentForm.value?.settings?.deal_settings?.stage_id ?? null,
      });

      // Pipelines for the picker — fetched once on mount
      const pipelinesLoading = computed(() => dealStore.pipelinesLoading);
      const pipelineOptions = computed(() =>
        (dealStore.pipelines || []).map((p) => ({ value: p.id, label: p.name }))
      );

      // Stages of the currently-selected pipeline (sorted by sort_order)
      const stageOptions = computed(() => {
        const pipeline = (dealStore.pipelines || []).find(
          (p) => p.id === dealSettings.value.pipeline_id
        );
        const stages = [...(pipeline?.stages || [])].sort(
          (a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)
        );
        return stages.map((s) => ({ value: s.id, label: s.name }));
      });

      // When the pipeline changes, drop a stage_id that no longer exists in
      // the new pipeline. Empty stage_id is fine — backend then defaults to
      // the first stage.
      watch(
        () => dealSettings.value.pipeline_id,
        (newPipelineId, oldPipelineId) => {
          if (newPipelineId === oldPipelineId) return;
          const validStageIds = stageOptions.value.map((s) => s.value);
          if (
            dealSettings.value.stage_id &&
            !validStageIds.includes(dealSettings.value.stage_id)
          ) {
            dealSettings.value.stage_id = null;
          }
        }
      );

      // When the deal-toggle is turned OFF, also force the proposal-toggle off:
      // a proposal without an underlying deal makes no functional sense in this
      // flow, and the UI hides the offerte sub-section anyway.
      watch(
        () => dealSettings.value.create_deal,
        (createDeal) => {
          if (!createDeal && configuratorSettings.value?.autoCreateProposal) {
            configuratorSettings.value.autoCreateProposal = false;
          }
        }
      );

      // Configurator-specific settings
      const selectedDesignId = ref(currentForm.value?.design_id);
      const configuratorSettings = ref({
        showPricing: currentForm.value?.settings?.cpq?.showPricing ?? true,
        showImages: currentForm.value?.settings?.cpq?.designSettings?.showImages ?? true,
        enableUpsells: currentForm.value?.settings?.cpq?.designSettings?.enableUpsells ?? false,
        // Display settings
          showCategories: currentForm.value?.settings?.cpq?.displaySettings?.showCategories ?? false,
          categorizeUpsells:
            currentForm.value?.settings?.cpq?.displaySettings?.categorizeUpsells ?? false,
        autoCreateProposal: currentForm.value?.settings?.cpq?.autoCreateProposal ?? true,
        proposalType: currentForm.value?.settings?.cpq?.proposalType || 'definitive',
          showImagesOnProposal:
            currentForm.value?.settings?.cpq?.proposalSettings?.showImages ?? false,
        requireTermsAcceptance: currentForm.value?.settings?.cpq?.requireTermsAcceptance ?? false,
        showMarketingOptIn: currentForm.value?.settings?.cpq?.showMarketingOptIn ?? false,
        cart: {
          enabled: currentForm.value?.settings?.cpq?.cart?.enabled ?? false,
          showPricing: currentForm.value?.settings?.cpq?.cart?.showPricing ?? true,
          allowQuantities: currentForm.value?.settings?.cpq?.cart?.allowQuantities ?? true,
          volumePricingRules: currentForm.value?.settings?.cpq?.cart?.volumePricingRules || [],
          showTotals: currentForm.value?.settings?.cpq?.cart?.showTotals ?? 'show',
            priceRange: currentForm.value?.settings?.cpq?.cart?.priceRange || {
              enabled: false,
              type: 'percentage',
              percentage: 20,
              percentageDirection: 'both',
              minPrice: 0,
              maxPrice: 0,
              marginPercentage: 0,
              displayMode: 'full',
              roundTo: null,
              labels: {
                minOnly: 'Vanaf',
                maxOnly: 'Tot',
                maxUntil: 'Max.',
              },
            },
            revealPricesAfterSubmit: currentForm.value?.settings?.cpq?.cart?.revealPricesAfterSubmit ?? false,
            priceRangeAfterSubmit: currentForm.value?.settings?.cpq?.cart?.priceRangeAfterSubmit || {
              enabled: false,
              type: 'percentage',
              percentage: 20,
              percentageDirection: 'both',
              minPrice: 0,
              maxPrice: 0,
              marginPercentage: 0,
              displayMode: 'full',
              roundTo: null,
              labels: {
                minOnly: 'Vanaf',
                maxOnly: 'Tot',
                maxUntil: 'Max.',
              },
            },
        },
      });

      const availableDesigns = computed(() => designStore.designs);
      const currentDesign = computed(() => {
        if (!selectedDesignId.value) return null;
        return availableDesigns.value.find((d) => d.id === selectedDesignId.value);
      });
      const selectedProductsCount = computed(() => {
        return selectedProductsData.value.length;
      });

      // Product selection state
      const productsLoading = ref(false);
      const availableProductsForSelection = ref([]);
      const selectedProductsData = ref([]);
      const productService = ref(null);

      // Normalize selectedProducts to array of objects with { productId, isMostChosen }
      const normalizeProductData = (products) => {
        if (!products || !Array.isArray(products)) return [];
        return products.map((item) => {
          if (typeof item === 'string') {
            return { productId: item, isMostChosen: false };
          }
          return { productId: item.productId || item.id, isMostChosen: item.isMostChosen || false };
        });
      };

      // Initialize selected products from form settings
      // Watch for changes in selectedProducts from backend, but only initialize if not already set
      // This prevents overwriting user changes during editing
      watch(
        () => currentForm.value?.settings?.cpq?.selectedProducts,
        (newProducts) => {
          // Only update if we're not currently saving
          if (!saving.value && newProducts) {
            const normalized = normalizeProductData(newProducts);
            // Only update if selectedProductsData is empty OR if the product IDs have changed
            // This preserves isMostChosen flags that the user has set
            const currentProductIds = selectedProductsData.value.map((p) => p.productId).sort();
            const newProductIds = normalized.map((p) => p.productId).sort();
            const productIdsChanged =
              JSON.stringify(currentProductIds) !== JSON.stringify(newProductIds);

            if (selectedProductsData.value.length === 0 || productIdsChanged) {
              selectedProductsData.value = normalized;
            } else {
              // Product IDs are the same, but we might need to merge isMostChosen from backend
              // Only update isMostChosen if it's explicitly set in backend (not just false)
              normalized.forEach((newProduct) => {
                const existingProduct = selectedProductsData.value.find(
                  (p) => p.productId === newProduct.productId
                );
                if (existingProduct && newProduct.isMostChosen === true) {
                  // Backend explicitly set this as most chosen, update it
                  existingProduct.isMostChosen = true;
                }
              });
            }
          }
        },
        { immediate: true }
      );

      const initializeSettings = () => {
        programmaticUpdate.value = true;
        const currentSettings = currentForm.value?.settings || {};
        Object.keys(inputs.value).forEach((key) => {
          if (currentSettings[key] !== undefined) {
            // Convert array to comma-separated string for notificationRecipients
            if (key === 'notificationRecipients' && Array.isArray(currentSettings[key])) {
              inputs.value[key].binding = currentSettings[key].join(', ');
            } else {
              inputs.value[key].binding = currentSettings[key];
            }
          }
        });

        // For configurators: auto-set recipient email field to 'email-input' if not set
        if (
          isConfigurator.value &&
          !inputs.value.recipientEmailFieldId.binding &&
          inputs.value.enableRecipientNotification.binding
        ) {
          inputs.value.recipientEmailFieldId.binding = 'email-input';
        }

        // Initialize configurator settings
        if (isConfigurator.value && currentForm.value?.settings?.cpq) {
          configuratorSettings.value = {
            showPricing: currentForm.value.settings.cpq.showPricing ?? true,
            showImages: currentForm.value.settings.cpq.designSettings?.showImages ?? true,
            enableUpsells: currentForm.value.settings.cpq.designSettings?.enableUpsells ?? false,
            // Display settings
            showCategories: currentForm.value.settings.cpq.displaySettings?.showCategories ?? false,
            categorizeUpsells:
              currentForm.value.settings.cpq.displaySettings?.categorizeUpsells ?? false,
            autoCreateProposal: currentForm.value.settings.cpq.autoCreateProposal ?? true,
            proposalType: currentForm.value.settings.cpq.proposalType || 'definitive',
            showImagesOnProposal:
              currentForm.value.settings.cpq.proposalSettings?.showImages ?? false,
            requireTermsAcceptance: currentForm.value.settings.cpq.requireTermsAcceptance ?? false,
            showMarketingOptIn: currentForm.value.settings.cpq.showMarketingOptIn ?? false,
            cart: {
              enabled: currentForm.value.settings.cpq.cart?.enabled ?? false,
              showPricing: currentForm.value.settings.cpq.cart?.showPricing ?? true,
              allowQuantities: currentForm.value.settings.cpq.cart?.allowQuantities ?? true,
              volumePricingRules: currentForm.value.settings.cpq.cart?.volumePricingRules || [],
              showTotals: currentForm.value.settings.cpq.cart?.showTotals ?? 'show',
              revealPricesAfterSubmit:
                currentForm.value.settings.cpq.cart?.revealPricesAfterSubmit ?? false,
              priceRange: currentForm.value.settings.cpq.cart?.priceRange || {
                enabled: false,
                type: 'percentage',
                percentage: 20,
                percentageDirection: 'both',
                minPrice: 0,
                maxPrice: 0,
                marginPercentage: 0,
                displayMode: 'full',
                roundTo: null,
                labels: {
                  minOnly: 'Vanaf',
                  maxOnly: 'Tot',
                  maxUntil: 'Max.',
                },
              },
              priceRangeAfterSubmit: currentForm.value.settings.cpq.cart?.priceRangeAfterSubmit || {
                enabled: false,
                type: 'percentage',
                percentage: 20,
                percentageDirection: 'both',
                minPrice: 0,
                maxPrice: 0,
                marginPercentage: 0,
                displayMode: 'full',
                roundTo: null,
                labels: {
                  minOnly: 'Vanaf',
                  maxOnly: 'Tot',
                  maxUntil: 'Max.',
                },
              },
            },
          };
        }

        // Reload deal settings from the form (only meaningful for configurators
        // but harmless on other form types). Default `create_deal=true` matches
        // the initial-setup default above.
        dealSettings.value = {
          create_deal: currentForm.value?.settings?.deal_settings?.create_deal ?? true,
          pipeline_id: currentForm.value?.settings?.deal_settings?.pipeline_id ?? null,
          stage_id: currentForm.value?.settings?.deal_settings?.stage_id ?? null,
        };

        proposalDefaults.value = {
          price_visibility:
            currentForm.value?.settings?.proposal_defaults?.price_visibility || '',
        };

        selectedDesignId.value = currentForm.value?.design_id;

        originalSettings.value = JSON.parse(
          JSON.stringify(
            Object.keys(inputs.value).reduce((acc, key) => {
              acc[key] = inputs.value[key].binding;
              return acc;
            }, {})
          )
        );
        originalConfiguratorSettings.value = JSON.parse(JSON.stringify(configuratorSettings.value));
        originalDealSettings.value = JSON.parse(JSON.stringify(dealSettings.value));
        originalProposalDefaults.value = JSON.parse(JSON.stringify(proposalDefaults.value));
        originalDesignId.value = selectedDesignId.value;

        programmaticUpdate.value = false;
      };

      // Watch for changes to the form and initialize settings
      watch(currentForm, () => {
        if (currentForm.value) {
          // Don't reinitialize if we're in the middle of saving
          if (!saving.value) {
            initializeSettings();
          }
        }
      });

      // Watch for recipient notification toggle - auto-set email field for configurators
      watch(
        () => inputs.value.enableRecipientNotification.binding,
        (enabled) => {
          if (
            isConfigurator.value &&
            enabled &&
            !inputs.value.recipientEmailFieldId.binding
          ) {
            inputs.value.recipientEmailFieldId.binding = 'email-input';
          }
        }
      );

      const saveSettings = async () => {
        saving.value = true;
        try {
          // For configurators: auto-set recipient email field if enabled but not set
          if (
            isConfigurator.value &&
            inputs.value.enableRecipientNotification.binding &&
            !inputs.value.recipientEmailFieldId.binding
          ) {
            inputs.value.recipientEmailFieldId.binding = 'email-input';
          }

          // Format settings to simple key-value pairs
          const formattedSettings = Object.keys(inputs.value).reduce((acc, key) => {
            // Convert comma-separated string to array for notificationRecipients
            if (key === 'notificationRecipients') {
              acc[key] = inputs.value[key].binding
                .split(',')
                .map((email) => email.trim())
                .filter((email) => email !== '');
            } else {
              acc[key] = inputs.value[key].binding;
            }
            return acc;
          }, {});

          // Deal-creation settings live alongside other top-level form settings
          // (NOT inside cpq) — backend reads `settings.deal_settings` to decide
          // whether to auto-create a deal on submission and in which pipeline /
          // stage. When create_deal is false, both pipeline_id and stage_id are
          // wiped to avoid dangling references.
          if (isConfigurator.value) {
            formattedSettings.deal_settings = {
              create_deal: !!dealSettings.value.create_deal,
              pipeline_id: dealSettings.value.create_deal
                ? dealSettings.value.pipeline_id || null
                : null,
              stage_id:
                dealSettings.value.create_deal && dealSettings.value.pipeline_id
                  ? dealSettings.value.stage_id || null
                  : null,
            };

            // Funnel override for quote presentation; '' / empty = inherit org.
            const existingProposalDefaults =
              currentForm.value?.settings?.proposal_defaults || {};
            const visibility = proposalDefaults.value.price_visibility || null;
            formattedSettings.proposal_defaults = {
              ...existingProposalDefaults,
              price_visibility: visibility || null,
            };
          }

          await formStore.updateGeneralSettings(formId, formattedSettings);

          // Update CPQ settings if configurator
          if (isConfigurator.value) {
            // Merge with existing CPQ settings to preserve any fields we're not updating
            const existingCPQSettings = currentForm.value?.settings?.cpq || {};
            const cpqSettings = {
              ...existingCPQSettings,
              showPricing: configuratorSettings.value.showPricing,
              autoCreateProposal: configuratorSettings.value.autoCreateProposal,
              proposalType: configuratorSettings.value.proposalType,
              requireTermsAcceptance: configuratorSettings.value.requireTermsAcceptance,
              showMarketingOptIn: configuratorSettings.value.showMarketingOptIn,
              designSettings: {
                ...existingCPQSettings.designSettings,
                showImages: configuratorSettings.value.showImages,
                enableUpsells: configuratorSettings.value.enableUpsells,
              },
              displaySettings: {
                ...existingCPQSettings.displaySettings,
                showCategories: configuratorSettings.value.showCategories,
                categorizeUpsells: configuratorSettings.value.categorizeUpsells,
              },
              proposalSettings: {
                ...existingCPQSettings.proposalSettings,
                showImages: configuratorSettings.value.showImagesOnProposal,
              },
              cart: {
                ...existingCPQSettings.cart,
                enabled: configuratorSettings.value.cart.enabled,
                showPricing: configuratorSettings.value.cart.showPricing,
                allowQuantities: configuratorSettings.value.cart.allowQuantities,
                volumePricingRules: configuratorSettings.value.cart.volumePricingRules || [],
                showTotals: configuratorSettings.value.cart.showTotals,
                revealPricesAfterSubmit: configuratorSettings.value.cart.revealPricesAfterSubmit,
                priceRange: configuratorSettings.value.cart.priceRange,
                priceRangeAfterSubmit: configuratorSettings.value.cart.priceRangeAfterSubmit,
              },
              selectedProducts: JSON.parse(JSON.stringify(selectedProductsData.value)), // Deep copy to ensure reactivity
            };

            await formStore.updateCPQSettings(formId, cpqSettings);

            // Update design_id if changed
            if (selectedDesignId.value !== currentForm.value.design_id) {
              formStore.currentForm.design_id = selectedDesignId.value;
              await formStore.saveCurrentForm();
            }

            // Reload form to get the latest data from backend
            await formStore.loadForm(formId);
            // Ensure selectedProductsData is correctly initialized from reloaded form
            if (formStore.currentForm?.settings?.cpq?.selectedProducts) {
              selectedProductsData.value = normalizeProductData(
                formStore.currentForm.settings.cpq.selectedProducts
              );
            }
          }

          originalSettings.value = JSON.parse(
            JSON.stringify(
              Object.keys(inputs.value).reduce((acc, key) => {
                acc[key] = inputs.value[key].binding;
                return acc;
              }, {})
            )
          );
          originalConfiguratorSettings.value = JSON.parse(
            JSON.stringify(configuratorSettings.value)
          );
          originalDealSettings.value = JSON.parse(JSON.stringify(dealSettings.value));
          originalProposalDefaults.value = JSON.parse(JSON.stringify(proposalDefaults.value));
          originalDesignId.value = selectedDesignId.value;
          hasChanges.value = false;

          toastStore.addToast({
            message: 'Instellingen succesvol opgeslagen',
            type: 'success',
          });
        } catch (error) {
          toastStore.addToast({
            message: 'Er is een fout opgetreden bij het opslaan',
            type: 'error',
          });
        } finally {
          saving.value = false;
        }
      };

      // Helper function to check for changes
      const checkForChanges = () => {
        if (programmaticUpdate.value) {
          return;
        }

        const currentValues = Object.keys(inputs.value).reduce((acc, key) => {
          acc[key] = inputs.value[key].binding;
          return acc;
        }, {});

        const inputsChanged =
          JSON.stringify(currentValues) !== JSON.stringify(originalSettings.value);
        const configuratorChanged =
          JSON.stringify(configuratorSettings.value) !==
          JSON.stringify(originalConfiguratorSettings.value);
        const dealChanged =
          JSON.stringify(dealSettings.value) !== JSON.stringify(originalDealSettings.value);
        const proposalDefaultsChanged =
          JSON.stringify(proposalDefaults.value) !==
          JSON.stringify(originalProposalDefaults.value);
        const designChanged = selectedDesignId.value !== originalDesignId.value;

        hasChanges.value =
          inputsChanged ||
          configuratorChanged ||
          dealChanged ||
          proposalDefaultsChanged ||
          designChanged;
      };

      // Watch inputs
      watch(
        inputs,
        () => {
          checkForChanges();
        },
        { deep: true }
      );

      // Watch deal settings
      watch(
        dealSettings,
        () => {
          checkForChanges();
        },
        { deep: true }
      );

      watch(
        proposalDefaults,
        () => {
          checkForChanges();
        },
        { deep: true }
      );

      // Watch configurator settings (including nested cart)
      watch(
        configuratorSettings,
        () => {
          checkForChanges();
        },
        { deep: true, immediate: false }
      );

      // Watch cart settings specifically (extra watch for nested changes)
      watch(
        () => configuratorSettings.value.cart,
        () => {
          checkForChanges();
        },
        { deep: true }
      );

      // Watch design
      watch(selectedDesignId, () => {
        checkForChanges();
      });

      // Watch requireTermsAcceptance and validate that terms_url is set
      watch(
        () => configuratorSettings.value.requireTermsAcceptance,
        (newValue) => {
          if (newValue === true) {
            const organization = userStore.currentOrganization;
            const termsUrl = organization?.settings?.terms_url;
            if (!termsUrl || termsUrl.trim() === '') {
              toastStore.addToast({
                message:
                  'Je moet eerst een URL naar de algemene voorwaarden instellen in de Organization Settings voordat je deze optie kunt inschakelen.',
                type: 'warning',
                duration: 5000,
              });
              // Reset the toggle
              configuratorSettings.value.requireTermsAcceptance = false;
            }
          }
        }
      );

      const selectDesign = (designId) => {
        selectedDesignId.value = designId;
      };

      const handleCreateDesign = () => {
        const modalId = uiStore.addModal(SidebarDesign, {
          title: 'Nieuw Design Aanmaken',
          size: 'xl',
          modalMode: true,
          existingDesign: null,
          startEditing: true,
          onSave: async (createdDesign) => {
            try {
              await designStore.fetchDesigns();
              selectedDesignId.value = createdDesign.id;
              toastStore.addToast({
                message: 'Design succesvol aangemaakt',
                type: 'success',
              });
              uiStore.closeModal(modalId);
            } catch (error) {
              alert('Er is een fout opgetreden bij het aanmaken van het design');
            }
          },
          onCancel: () => {
            uiStore.closeModal(modalId);
          },
        });
      };

      const handleEditDesign = (design) => {
        const modalId = uiStore.addModal(SidebarDesign, {
          title: 'Design Bewerken',
          size: 'xl',
          modalMode: true,
          existingDesign: design,
          startEditing: true,
          onSave: async (updatedDesign) => {
            try {
              await designStore.fetchDesigns();
              if (updatedDesign?.id) {
                selectedDesignId.value = updatedDesign.id;
              }
              toastStore.addToast({
                message: 'Design succesvol bijgewerkt',
                type: 'success',
              });
              uiStore.closeModal(modalId);
            } catch (error) {
              alert('Er is een fout opgetreden bij het bijwerken van het design');
            }
          },
          onCancel: () => {
            uiStore.closeModal(modalId);
          },
        });
      };

      // Load all available products
      const loadProductsForSelection = async () => {
        if (!isConfigurator.value) return;
        try {
          productsLoading.value = true;
          if (!productService.value) {
            const { getProductService } = await import('@/composables/useProductService');
            productService.value = await getProductService();
          }
          const products = await productService.value.getAllProducts();
          availableProductsForSelection.value = products || [];
        } catch (error) {
          availableProductsForSelection.value = [];
        } finally {
          productsLoading.value = false;
        }
      };

      const getProductName = (productId) => {
        const product = availableProductsForSelection.value.find((p) => p.id === productId);
        return product?.title || product?.name || productId || 'Onbekend product';
      };

      const getProduct = (productId) => {
        return availableProductsForSelection.value.find((p) => p.id === productId) || null;
      };

      const unselectedProducts = computed(() =>
        availableProductsForSelection.value.filter((p) => !isProductSelected(p.id))
      );

      const volumePricingGroupOptions = computed(() => {
        const groups = new Map();

        availableProductsForSelection.value.forEach((product) => {
          (product.categories || []).forEach((category) => {
            if (category?.id) {
              groups.set(category.id, {
                value: category.id,
                label: category.name || category.slug || category.id,
              });
            }
          });

          (product.categoryIds || []).forEach((categoryId) => {
            if (!groups.has(categoryId)) {
              groups.set(categoryId, {
                value: categoryId,
                label: categoryId,
              });
            }
          });

          if (product.category && !groups.has(product.category)) {
            groups.set(product.category, {
              value: product.category,
              label: product.category,
            });
          }
        });

        return Array.from(groups.values()).sort((a, b) => a.label.localeCompare(b.label));
      });

      // Product selection methods
      const isProductSelected = (productId) => {
        return selectedProductsData.value.some((p) => p.productId === productId);
      };

      const isMostChosen = (productId) => {
        const product = selectedProductsData.value.find((p) => p.productId === productId);
        return product?.isMostChosen || false;
      };

      const toggleProduct = (productId) => {
        const index = selectedProductsData.value.findIndex((p) => p.productId === productId);
        if (index > -1) {
          selectedProductsData.value.splice(index, 1);
        } else {
          selectedProductsData.value.push({ productId, isMostChosen: false });
        }
        hasChanges.value = true;
      };

      const toggleMostChosen = (productId, newValue) => {
        const product = selectedProductsData.value.find((p) => p.productId === productId);
        if (product) {
          // Use the new value from ToggleInput
          if (newValue === true) {
            // If turning on "most chosen", turn off all others
            selectedProductsData.value.forEach((p) => {
              p.isMostChosen = false;
            });
            product.isMostChosen = true;
          } else {
            // If turning off, just turn it off (can be 0 most chosen)
            product.isMostChosen = false;
          }

          // Force reactivity by creating a new array reference
          selectedProductsData.value = [...selectedProductsData.value];

          hasChanges.value = true;
        }
      };

      const formatProductPrice = (price) => {
        return new Intl.NumberFormat('nl-NL', {
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        }).format(price);
      };

      const handleProductImageError = (event) => {
        event.target.style.display = 'none';
      };

      const handleGoToProducts = () => {
        router.push('/products');
      };

      onMounted(async () => {
        // Load form if not already loaded
        if (!currentForm.value || currentForm.value.id !== formId) {
          await formStore.loadForm(formId);
        }

        // Ensure designs are loaded if configurator
        if (isConfigurator.value && availableDesigns.value.length === 0) {
          await designStore.fetchDesigns();
        }

        // Load products if configurator
        if (isConfigurator.value) {
          await loadProductsForSelection();
        }

        // Load pipelines for the deal-creation pipeline picker (only relevant
        // for configurators; cheap and cached at the store level so safe to
        // call again if pipelines were already loaded elsewhere).
        if (isConfigurator.value && (dealStore.pipelines?.length ?? 0) === 0) {
          try {
            await dealStore.fetchPipelines();
          } catch (err) {
            console.warn('Failed to load pipelines for deal-settings picker:', err);
          }
        }

        initializeSettings();
      });

      // Watch for products tab activation to load products if needed
      watch(activeTab, (newTab) => {
        if (
          newTab === 'products' &&
          isConfigurator.value &&
          availableProductsForSelection.value.length === 0
        ) {
          loadProductsForSelection();
        }
      });

      const exportFormData = async () => {
        exporting.value = true;
        try {
          // Prepare the data for export
          const formPages = currentForm.value?.pages || [];

          // Create a flattened structure for Excel
          const flattenedData = formPages
            .map((page) => {
              const elements = page.elements.map((element) => ({
                pageTitle: page.title,
                elementId: element.id,
                elementType: element.type,
                elementLabel: element.props.label,
                elementRequired: element.props.required || false,
                elementConditions: JSON.stringify(element.props.conditions),
                // Add specific properties based on element type
                ...(element.type === 'single-choice' || element.type === 'multiple-choice'
                  ? {
                      choices: JSON.stringify(element.props.choices),
                    }
                  : {}),
                ...(element.type === 'formula'
                  ? {
                      formula: element.props.formula,
                      displayAsCurrency: element.props.display_as_currency,
                    }
                  : {}),
              }));
              return elements;
            })
            .flat(); // Flatten the array of arrays

          // Create the worksheet
          const worksheet = XLSX.utils.json_to_sheet(flattenedData);

          // Adjust column widths
          const colWidths = [
            { wch: 15 }, // pageTitle
            { wch: 20 }, // elementId
            { wch: 15 }, // elementType
            { wch: 30 }, // elementLabel
            { wch: 10 }, // elementRequired
            { wch: 40 }, // elementConditions
            { wch: 50 }, // choices/formula
          ];
          worksheet['!cols'] = colWidths;

          // Create and save the workbook
          const workbook = XLSX.utils.book_new();
          XLSX.utils.book_append_sheet(workbook, worksheet, 'FormStructure');
          XLSX.writeFile(workbook, `form-${currentForm.value.id}-structure.xlsx`);
        } catch (error) {
          toastStore.addToast({
            message: 'Failed to export form data',
            type: 'error',
          });
        } finally {
          exporting.value = false;
        }
      };

      const handleFileImport = (event) => {
        const file = event.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (e) => {
          Papa.parse(e.target.result, {
            complete: (results) => {
              // Here you can handle the parsed data
            },
            header: true,
            skipEmptyLines: true,
          });
        };
        reader.readAsText(file);
      };

      // Add computed property for email field options
      const emailFieldOptions = computed(() => {
        const options = [];
        
        // Add email fields from existing form pages
        if (currentForm.value?.pages) {
          currentForm.value.pages.forEach((page) => {
            page.elements.forEach((element) => {
              if (element.type === 'email') {
                options.push({
                  label: element.props.label || element.id,
                  value: element.id,
                });
              }
            });
          });
        }
        
        // For configurators: add the auto-generated contact-details email field
        // This field is generated at runtime, so it might not be in currentForm.value.pages
        if (isConfigurator.value) {
          const configuratorEmailFieldId = 'email-input';
          // Only add if not already in options (to avoid duplicates)
          if (!options.find((opt) => opt.value === configuratorEmailFieldId)) {
            options.push({
              label: 'E-mailadres',
              value: configuratorEmailFieldId,
            });
          }
        }
        
        return options;
      });

      const addParameter = () => {
        inputs.value.parameters.binding.push({
          name: '',
          description: '',
          nameError: null,
        });
      };

      const removeParameter = (index) => {
        inputs.value.parameters.binding.splice(index, 1);
      };

      const validateParameterName = (index, event) => {
        const param = inputs.value.parameters.binding[index];
        param.nameError = event.error;
      };

      const getExampleUrl = () => {
        const baseUrl = window.location.origin + '/form/' + formId;
        const params = inputs.value.parameters.binding
          .filter((p) => p.name)
          .map((p) => `${p.name}=value`)
          .join('&');
        return params ? `${baseUrl}?${params}` : baseUrl;
      };

      return {
        inputs,
        activeTab,
        hasChanges,
        saving,
        onValidate,
        saveSettings,
        exportFormData,
        exporting,
        handleFileImport,
        emailFieldOptions,
        addParameter,
        removeParameter,
        validateParameterName,
        getExampleUrl,
        isConfigurator,
        isMeasurementWorkflow,
        proposalTypeOptions,
        priceVisibilityOptions,
        proposalDefaults,
        configuratorSettings,
        dealSettings,
        pipelineOptions,
        pipelinesLoading,
        stageOptions,
        selectedDesignId,
        availableDesigns,
        currentDesign,
        selectedProductsCount,
        selectDesign,
        handleCreateDesign,
        handleEditDesign,
        productsLoading,
        availableProductsForSelection,
        selectedProductsData,
        volumePricingGroupOptions,
        getProductName,
        getProduct,
        unselectedProducts,
        isProductSelected,
        isMostChosen,
        toggleProduct,
        toggleMostChosen,
        formatProductPrice,
        handleProductImageError,
        handleGoToProducts,
        $t: t, // Make translation function available in template
      };
    },
  };
</script>

<style scoped lang="scss">
  .settings-page {
    display: flex;
    padding: 2rem;
  }
  .settings-container {
    background: #fff;
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    display: flex;
    width: 100%;
    min-height: calc(100vh - 4rem);
    max-width: 800px;
    margin: 0 auto;
  }

  .settings-sidebar {
    width: 240px;
    border-right: 1px solid #eee;
    padding: 2rem 0;
  }

  .settings-tabs {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    padding: 0 1rem;
  }
  .tab {
    padding: 0.75rem 1rem;
    text-align: left;
    background: none;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    font-size: 0.95rem;
    color: #666;
    transition: all 0.2s ease;

    &:hover {
      background: #f5f5f5;
      color: #333;
    }

    &.active {
      background: #f0f7ff;
      color: #0066cc;
      font-weight: 500;
    }
  }

  .settings-content {
    flex: 1;
    padding: 2rem;
    position: relative;
  }

  .settings-grid {
    display: grid;
    gap: 1.5rem;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  }

  .settings-subsection {
    padding: 1rem;
    background: var(--slate-50);
    border: 1px solid var(--slate-200);
    border-radius: 0.75rem;

    &--full {
      grid-column: 1 / -1;
    }

    h4 {
      margin: 0 0 0.35rem;
      color: var(--slate-900);
      font-size: 1rem;
      font-weight: 600;
    }
  }

  .form-row {
    padding: 1rem 0;
    border-bottom: 1px solid var(--slate-200);
  }

  .settings-footer {
    padding: 1rem;
    background: white;
    border-top: 1px solid #eee;
    text-align: right;
    margin-top: 2rem;
  }

  .notification-group {
    &__card {
      background: white;
      border-radius: 12px;
      padding: 1.5rem;
      margin-bottom: 1.5rem;
      border: 1px solid var(--slate-200);
    }

    &__header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      margin-bottom: 1rem;
    }

    &__title {
      display: flex;
      gap: 1rem;
      align-items: flex-start;

      .icon {
        color: var(--slate-600);
      }
    }

    &__title-content {
      h3 {
        margin: 0;
        font-size: 1.125rem;
        font-weight: 600;
        color: var(--slate-900);
      }
    }

    &__subtitle {
      margin: 0.25rem 0 0;
      font-size: 0.875rem;
      color: var(--slate-600);
    }

    &__content {
      margin-top: 1rem;
    }

    &__hint {
      margin: 0.35rem 0 1rem;
      font-size: 0.8125rem;
      color: var(--slate-500);
      line-height: 1.4;
    }
  }

  .section-description {
    color: var(--slate-600);
    margin-bottom: 1.5rem;
  }

  .settings-section-divider {
    height: 1px;
    background: var(--slate-200);
    margin: 2rem 0;
  }

  .nested-section {
    margin-top: 2rem;
    margin-left: 1rem;
    padding: 1rem 1.25rem 1.25rem;
    border-left: 2px solid var(--slate-200);
    background: var(--slate-50);
    border-radius: 0 8px 8px 0;

    &__title {
      margin: 0 0 0.25rem;
      font-size: 1rem;
      font-weight: 600;
      color: var(--slate-800);
    }

    &__description {
      margin-bottom: 1rem;
    }
  }

  .toggle-with-description {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .toggle-description {
    margin: 0;
    font-size: 0.875rem;
    color: var(--slate-500);
    padding-left: 3rem;

    &--warning {
      color: var(--orange-600);
    }
  }

  .product-summary {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1rem;
    background: var(--slate-50);
    border-radius: 8px;
    border: 1px solid var(--slate-200);

    .summary-item {
      display: flex;
      flex-direction: column;
      gap: 0.25rem;

      label {
        font-size: 0.875rem;
        font-weight: 500;
        color: var(--slate-600);
      }

      .badge {
        display: inline-flex;
        align-items: center;
        padding: 0.25rem 0.75rem;
        background: var(--brand-100);
        color: var(--brand-800);
        border-radius: 9999px;
        font-size: 0.75rem;
        font-weight: 500;
        width: fit-content;
      }
    }
  }

  .product-selection {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .loading-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 3rem;
    gap: 1rem;
    color: var(--slate-600);
  }

  .product-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 1rem;
  }

  .product-card {
    display: flex;
    gap: 1rem;
    padding: 1rem;
    border: 2px solid var(--slate-200);
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s ease;
    background: white;

    &:hover {
      border-color: var(--brand-300);
      box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
    }

    &--selected {
      border-color: var(--brand-500);
      background: var(--brand-50);
    }

    &__image {
      width: 64px;
      height: 64px;
      flex-shrink: 0;
      border-radius: 6px;
      overflow: hidden;
      background: var(--slate-100);
      display: flex;
      align-items: center;
      justify-content: center;

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }

      .product-card__placeholder {
        color: var(--slate-400);
      }
    }

    &__content {
      flex: 1;
      min-width: 0;

      &__header {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        gap: 1rem;
        margin-bottom: 0.5rem;
      }

      h4 {
        margin: 0;
        color: var(--slate-800);
        font-size: 1rem;
        font-weight: 600;
        flex: 1;
      }

      .most-chosen-control {
        flex-shrink: 0;
        display: flex;
        align-items: center;

        :deep(.form-group) {
          margin: 0;
        }

        :deep(.sf-input) {
          margin: 0;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        :deep(.sf-input__label) {
          font-size: 0.75rem;
          color: var(--slate-600);
          font-weight: 500;
          margin: 0;
          white-space: nowrap;
        }

        :deep(.toggle) {
          flex-shrink: 0;
        }
      }

      .product-description {
        margin: 0 0 0.5rem 0;
        color: var(--slate-600);
        font-size: 0.875rem;
        line-height: 1.4;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
      }

      .product-meta {
        display: flex;
        gap: 1rem;
        font-size: 0.875rem;

        .product-options {
          color: var(--slate-500);
        }

        .product-price {
          font-weight: 600;
          color: var(--brand-600);
        }
      }
    }

    &__checkbox {
      flex-shrink: 0;
      display: flex;
      align-items: center;
    }
  }

  .product-selection__main {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
  }

  .selected-products-draggable {
    &__title {
      margin: 0 0 0.75rem 0;
      font-size: 0.875rem;
      font-weight: 600;
      color: var(--slate-700);
    }

    &__list {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
      gap: 1rem;
    }
  }

  .product-card {
    &__handle {
      position: absolute;
      top: 0.5rem;
      left: 0.5rem;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      padding: 0.25rem;
      color: var(--slate-400);
      cursor: grab;
      border-radius: 4px;
      z-index: 1;
      background: rgba(255, 255, 255, 0.9);

      &:active {
        cursor: grabbing;
      }

      &:hover {
        color: var(--slate-600);
        background: var(--slate-100);
      }
    }

    &__remove {
      flex-shrink: 0;
    }

    &--in-order {
      position: relative;
      padding-left: 2.25rem;
      display: flex;
      align-items: stretch;
      gap: 0.75rem;

      .product-card__content {
        flex: 1;
        min-width: 0;
      }
    }

    &--ghost {
      opacity: 0.6;
      .product-card__content,
      .product-card__image {
        pointer-events: none;
      }
    }

    &--dragging {
      box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
      z-index: 10;
    }
  }

  .product-grid-wrapper {
    &__title {
      margin: 0 0 0.75rem 0;
      font-size: 0.875rem;
      font-weight: 600;
      color: var(--slate-700);
    }
  }

  .selected-summary {
    padding: 1rem;
    background: var(--brand-50);
    border-radius: 8px;
    border: 1px solid var(--brand-200);
    text-align: center;
    color: var(--brand-800);
    font-size: 0.875rem;
  }

  .design-selection {
    .current-design {
      margin-bottom: 1.5rem;
      padding: 1rem;
      background: var(--slate-50);
      border-radius: 8px;
      border: 1px solid var(--slate-200);

      .design-preview {
        display: flex;
        align-items: center;
        gap: 1rem;

        .design-color-preview {
          width: 40px;
          height: 40px;
          border-radius: 6px;
          flex-shrink: 0;
        }

        .design-info {
          display: flex;
          flex-direction: column;

          .design-name {
            font-weight: 600;
            color: var(--slate-800);
          }

          .design-category {
            font-size: 0.875rem;
            color: var(--slate-500);
            text-transform: capitalize;
          }
        }
      }
    }

    .designs-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
      gap: 1rem;
      margin-bottom: 1rem;
    }

    .design-option {
      padding: 1rem;
      border: 2px solid var(--slate-200);
      border-radius: 8px;
      cursor: pointer;
      transition: all 0.2s ease;
      position: relative;
      background: white;

      &:hover {
        border-color: var(--brand-300);
      }

      &--active {
        border-color: var(--brand-500);
        background: var(--brand-50);
      }

      .design-color {
        width: 100%;
        height: 60px;
        border-radius: 4px;
        margin-bottom: 0.75rem;
      }

      .design-details {
        display: flex;
        flex-direction: column;

        .design-name {
          font-weight: 500;
          color: var(--slate-800);
          margin-bottom: 0.25rem;
        }

        .design-category {
          font-size: 0.75rem;
          color: var(--slate-500);
          text-transform: capitalize;
        }
      }

      &__actions {
        position: absolute;
        top: 0.5rem;
        right: 0.5rem;
        display: flex;
        align-items: center;
        gap: 0.25rem;
      }

      .design-edit-btn {
        opacity: 0;
        transition: opacity 0.2s ease;
      }

      &:hover .design-edit-btn,
      &--active .design-edit-btn {
        opacity: 1;
      }

      .design-selected {
        position: static;
        width: 24px;
        height: 24px;
        background: var(--brand-500);
        color: white;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 0.75rem;
      }
    }

    .design-actions {
      display: flex;
      gap: 1rem;
      flex-wrap: wrap;

      .btn--active {
        background: var(--brand-50);
        border-color: var(--brand-500);
        color: var(--brand-700);
      }
    }
  }

  .parameters {
    display: flex;
    flex-direction: column;
    gap: 1rem;

    &__item {
      display: flex;
      gap: 1rem;
      align-items: flex-start;
      padding: 1rem;
      background: var(--slate-50);
      border-radius: 8px;
    }

    &__fields {
      flex: 1;
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 1rem;
    }

    &__remove {
      background: none;
      border: none;
      color: var(--slate-400);
      cursor: pointer;
      padding: 0.5rem;
      border-radius: 4px;
      margin-top: 2rem;

      &:hover {
        color: var(--red-500);
        background: var(--slate-100);
      }
    }

    &__add {
      margin-top: 1rem;
    }

    &__help {
      margin-top: 2rem;
      padding: 1.5rem;
      background: var(--slate-50);
      border-radius: 8px;
    }

    &__help-title {
      margin: 0 0 1rem;
      font-size: 1rem;
    }

    &__help-list {
      margin: 0.5rem 0;
      padding-left: 1.5rem;
    }

    &__help-text {
      margin: 0.5rem 0;
    }

    &__example {
      display: block;
      padding: 0.75rem;
      background: var(--slate-100);
      border-radius: 4px;
      font-family: monospace;
      margin-top: 0.5rem;
      word-break: break-all;
    }
  }
</style>
